import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import UAParser from 'ua-parser-js';
import {
  analyticsDB,
  convertBytes,
  EventNames,
  EventProperties,
  posthog,
  syncStateManager,
  userCache,
  VersionType,
} from '..';

class AnalyticsService {
  async trackEvent(name: EventNames, properties: EventProperties = {}) {
    try {
      if (process.env.NX_PUBLIC_ENV === 'passwordlessLocal') {
        return;
      }

      const parser = new UAParser();
      const device = parser.getDevice();
      const browser = parser.getBrowser();
      const os = parser.getOS();
      const info = await Device.getInfo();
      const isNative = Capacitor.isNativePlatform();
      const { value } = await Preferences.get({
        key: VersionType.Installed,
      });
      const appVersion = isNative ? (await App.getInfo()).version : 'N/A';

      //Todo implement better approach for desktop detection. currently using code snippet from: https://github.com/faisalman/ua-parser-js/issues/182#issuecomment-1708138242
      const isDesktop =
        device.type === undefined ||
        !['wearable', 'mobile'].includes(device.type);
      await analyticsDB.trackEvent({
        name,
        sentAt: new Date(),
        properties: {
          isNativeApp: isNative,
          buildVersion: value || 'N/A',
          appVersion: appVersion,
          deviceId: userCache.getDeviceId(),
          currentUrl: window.location.href,
          browser: browser.name!,
          browserVersion: browser.version!,
          os: isNative ? info.operatingSystem : os.name!,
          osVersion: isNative ? info.osVersion : os.version!,
          device: Capacitor.getPlatform(),
          deviceType: isDesktop ? 'desktop' : device.type!, //todo check device.type works on older phones or not.
          deviceName: info.name || 'N/A',
          screenHeight: window.screen.height,
          screenWidth: window.screen.width,
          viewportHeight: window.innerHeight,
          viewportWidth: window.innerWidth,
          totalFreeSpace: convertBytes(info.realDiskFree),
          totalSpace: convertBytes(info.realDiskTotal),
          isOffline: !syncStateManager.isConnected,
          $sentAt: new Date().toISOString(),
          $sessionId: posthog?.get_session_id() ?? '',
          // same as session_id for session recording.
          sessionId: posthog?.get_session_id() ?? '',
          ...properties,
        },
      });
    } catch (error) {
      console.error('Unable to add event to analyticsDB', error);
    }
  }
}

export const analyticsService = new AnalyticsService();
